import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, Clock, ShieldCheck, Lock, ChevronDown, ChevronUp, Heart, ChevronRight, Footprints, GraduationCap } from 'lucide-react';
const CHECKOUT_URL = 'https://afrekenen.stresslessdogs.nl/checkout/stressless-wandelen-m-korting';
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const day = now.getDay();
      const diffToSunday = day === 0 ? 0 : 7 - day;
      const nextSunday = new Date(now);
      nextSunday.setDate(now.getDate() + diffToSunday);
      nextSunday.setHours(23, 59, 59, 999);
      const difference = nextSunday.getTime() - now.getTime();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference / (1000 * 60 * 60) % 24),
          minutes: Math.floor(difference / 1000 / 60 % 60),
          seconds: Math.floor(difference / 1000 % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  return timeLeft;
}
export function StresslessWandelenPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const courseImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/cS8Dp82nw9bgvjp4Tm4gfx/MC_SW_foto_2.png',
    alt: 'Ontspannen wandelen met je hond aan een losse lijn'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/43G5tgeSGqJzu7vhxyBGxT/MC_SW_foto_8.png',
    alt: 'Les 5 uit de cursus: Extra tips voor stevige trekkers'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/ehQTbcqq7j8ic9NyiyVvC8/MC_SW_foto_7.png',
    alt: 'Les: Leren lopen met een slappe lijn - de eerste stap'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/r3nRpfgd61JBaqBTB8tM2p/MC_SW_foto_6.png',
    alt: 'Uitleg over het stresslevel van je hond herkennen'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/pPQK41mnDGXP3s8zBbmmMv/MC_SW_foto_1.png',
    alt: 'Les 1 uit de cursus: Het begint met een goed tuig'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/4xnzLYTZjUA8X5m6itpPv2/MC_SW_foto_3.png',
    alt: 'Werken met de lijn in een drukke omgeving'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/nyu5SZPibXi6MZoLCQnD8j/MC_SW_foto_4.png',
    alt: 'Rustig en ontspannen snuffelen tijdens de wandeling'
  }];
  const [selectedImage, setSelectedImage] = useState(0);
  const trustpilotGridRef = useRef<HTMLDivElement>(null);
  const {
    days,
    hours,
    minutes,
    seconds
  } = useCountdown();
  useEffect(() => {
    if ((window as any).Trustpilot && trustpilotGridRef.current) {
      ;
      (window as any).Trustpilot.loadFromElement(trustpilotGridRef.current, true);
    }
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is deze cursus geschikt voor een puppy?',
    a: 'Ja, absoluut! Hoe eerder je begint met de juiste communicatie, hoe beter. Je voorkomt hiermee dat je puppy verkeerd gedrag aanleert.'
  }, {
    q: 'Kun je de cursus volgen met een senior hond?',
    a: 'Zeker. Honden zijn nooit te oud om te leren. Omdat we werken vanuit ontspanning en communicatie (en niet met fysieke correcties), is dit juist heel fijn voor oudere honden.'
  }, {
    q: 'Mijn hond is onzeker of bang, is dit geschikt?',
    a: 'Juist voor onzekere honden is deze methode ideaal. We bouwen aan zelfvertrouwen en een veilige band, waardoor de angst op straat drastisch zal verminderen.'
  }, {
    q: 'Mijn hond vertoont probleemgedrag, kan ik meedoen?',
    a: 'Ja. Trekken, uitvallen, blaffen of bevriezen zijn allemaal symptomen van stress of onbegrip. Door de oorzaak aan te pakken met onze methode, verdwijnt het probleemgedrag vaak vanzelf.'
  }, {
    q: 'Heb ik bepaalde materialen nodig?',
    a: 'Een goed passend Y-tuigje en een gewone, vaste lijn van minimaal 2 meter is alles wat je nodig hebt. Geen slipkettingen of anti-trektuigen!'
  }, {
    q: 'Hoe lang heb ik toegang?',
    a: 'Je krijgt levenslange toegang tot al het lesmateriaal, inclusief toekomstige updates.'
  }];
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  return <div className="min-h-screen bg-[#FAFAF7] font-sans text-gray-800 selection:bg-[#e85d75] selection:text-white pb-20 md:pb-0" data-id="element-2640">
      {/* 1. STICKY HEADER BAR */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#1a2b4a] text-white shadow-md" data-id="element-2641">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2" data-id="element-2642">
          <div className="flex items-center gap-2 text-sm font-medium" data-id="element-2643">
            <Clock className="w-4 h-4 text-[#e85d75] flex-shrink-0" data-id="element-2644" />
            <span data-id="element-2645">
              ⏰ Actie eindigt over:{' '}
              <span className="font-mono font-bold text-[#e85d75]" data-id="element-2646">
                {formatTime(days)}:{formatTime(hours)}:{formatTime(minutes)}:
                {formatTime(seconds)}
              </span>
            </span>
          </div>
          <a href={CHECKOUT_URL} className="text-white/80 hover:text-white text-xs sm:text-sm font-medium transition-colors sm:bg-[#e85d75] sm:hover:bg-[#d64c64] sm:text-white sm:px-4 sm:py-1.5 sm:rounded-full sm:font-bold whitespace-nowrap" data-id="element-2647">
            Bekijk de actie ↓
          </a>
        </div>
      </div>

      {/* 2. HERO SECTION */}
      <section className="pt-28 md:pt-32 pb-12 md:pb-20 px-4 bg-white" data-id="element-2648">
        <div className="max-w-5xl mx-auto flex flex-col md:grid md:grid-cols-2 md:gap-12 md:items-center" data-id="element-2649">
          <div className="w-full rounded-2xl overflow-hidden shadow-lg order-2 mt-8 mb-0 md:mt-0 md:mb-0 md:order-2 aspect-[10/7] md:aspect-square" data-id="element-2650">
            <img src="https://cdn.magicpatterns.com/uploads/pC14pGq8sFZiGRj4x9WmKf/Amee_wandelen_foto.png" alt="Amee wandelt ontspannen met haar hond" className="w-full h-full object-cover object-[50%_55%] md:object-center" data-id="element-2651" />
          </div>

          <div className="flex flex-col items-center text-center order-1 md:items-start md:text-left md:order-1" data-id="element-2652">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1a2b4a] leading-tight mb-4" data-id="element-2653">
              Maak van jullie wandeling (weer) het leukste moment van de dag!
            </h1>

            <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-2654">
              De complete online methode om wandelproblemen stap voor stap op te
              lossen - zonder dwang, zonder correcties.
            </p>

            <div className="inline-flex items-center gap-2 bg-red-50 text-[#e85d75] text-sm font-bold px-4 py-2 rounded-full mb-6 border border-red-100" data-id="element-2655">
              🔥 Alleen dit weekend: 50% korting
            </div>

            <div className="flex items-center justify-center md:justify-start gap-3 mb-2" data-id="element-2656">
              <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-2657">
                €87
              </span>
              <span className="text-5xl font-extrabold text-[#e85d75]" data-id="element-2658">
                €43,50
              </span>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-8" data-id="element-2659">
              t/m zondag 28 juni 23:59
            </p>

            <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl mb-4" data-id="element-2660">
              Ja, ik wil de cursus met 50% korting →
            </a>

            <div className="flex justify-center md:justify-start items-center gap-2 text-sm text-gray-500 font-medium" data-id="element-2661">
              <ShieldCheck className="w-4 h-4 text-green-600" data-id="element-2662" />
              14 dagen niet-goed-geld-terug garantie
            </div>
          </div>
        </div>
      </section>

      {/* 3. PROBLEM SECTION */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-2663">
        <div className="max-w-3xl mx-auto" data-id="element-2664">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a2b4a] mb-6 text-center md:text-left" data-id="element-2665">
            Zie je op tegen de dagelijkse wandelingen?
          </h2>

          <p className="text-base text-gray-700 mb-6" data-id="element-2666">
            Merk je bijvoorbeeld dat je hond:
          </p>

          <ul className="space-y-5 mb-8" data-id="element-2667">
            {['het liefst snel naar huis wil (of zelfs niet het huis uit wil zodra je de lijn pakt)', 'schrikt van geluiden, vreemde voorwerpen of onverwachte situaties', 'hard trekt aan de lijn', "blaft naar van alles (honden, katten, fietsers, scooters, auto's etc.)", 'opspringt tegen mensen'].map((point, i) => <li key={i} className="flex items-start" data-id="element-2668">
                <ChevronRight className="w-5 h-5 text-[#e85d75] mr-3 flex-shrink-0 mt-0.5" data-id="element-2669" />
                <span className="text-gray-700 text-base leading-relaxed" data-id="element-2670">
                  {point}
                </span>
              </li>)}
          </ul>

          <p className="text-base text-gray-700 leading-relaxed" data-id="element-2671">
            Je hebt waarschijnlijk al van alles geprobeerd. En je bent niet de
            enige. Veel baasjes grijpen uit wanhoop naar 'oplossingen' die het
            probleem eigenlijk alleen maar erger maken.
          </p>
        </div>
      </section>

      {/* 4. SOLUTION/PHILOSOPHY SECTION */}
      <section className="py-12 md:py-20 px-4 bg-white" data-id="element-2672">
        <div className="max-w-5xl mx-auto md:grid md:grid-cols-2 md:gap-12 md:items-center" data-id="element-2673">
          <div className="w-full rounded-2xl overflow-hidden shadow-lg mb-8 md:mb-0 aspect-square" data-id="element-2674">
            <img src="https://cdn.magicpatterns.com/uploads/iiXgePNM5Tv3dNugSrv7jx/Ontwerp_zonder_titel_(74).png" alt="Baasje zit ontspannen in het gras met haar hond" className="w-full h-full object-cover" data-id="element-2675" />
          </div>

          <div data-id="element-2676">
            <div className="space-y-5 text-base md:text-lg text-gray-700 leading-relaxed mb-8" data-id="element-2677">
              <p data-id="element-2678">
                Wandelen is teamwork. Het is geen strijd om wie de baas is, maar
                een samenwerking gebaseerd op liefdevolle communicatie en de
                juiste begeleiding bieden aan je hond.
              </p>
              <p data-id="element-2679">
                Wanneer je hem begrijpt en op de juiste manier begeleid zal je
                zien dat alles prettiger gaat. Zijn gedrag is namelijk geen
                ongehoorzaamheid, het is een uiting van spanning, angst of
                overprikkeling.
              </p>
              <p data-id="element-2680">
                In de Stressless Wandelen cursus leer je hoe herkent wat er
                speelt én leer je methodes waarmee je jouw hond kunt helpen
                ontspannen. Zonder dwang, zonder pijn, maar met wederzijds
                respect.
              </p>
            </div>

            <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto md:inline-flex bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-2681">
              Ja, ik wil de cursus met 50% korting →
            </a>
          </div>
        </div>
      </section>

      {/* 5. WAAROM DIT DE SLIMSTE KEUZE IS BIJ WANDELPROBLEMEN */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-2682">
        <div className="max-w-5xl mx-auto" data-id="element-2683">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a2b4a] mb-8 md:mb-12 text-center" data-id="element-2684">
            Waarom dit de slimste keuze is bij wandelproblemen
          </h2>

          <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-3 md:gap-6 mb-8 md:mb-12" data-id="element-2685">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start" data-id="element-2686">
              <div className="bg-red-50 p-3 rounded-full mb-4" data-id="element-2687">
                <Heart className="w-6 h-6 text-[#e85d75]" data-id="element-2688" />
              </div>
              <h3 className="font-bold text-[#1a2b4a] mb-2 text-lg" data-id="element-2689">
                Geen correcties of straf
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2690">
                De methode is gebaseerd op liefdevolle communicatie, niet op
                dwang of pijnlijke hulpmiddelen.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start" data-id="element-2691">
              <div className="bg-green-50 p-3 rounded-full mb-4" data-id="element-2692">
                <Footprints className="w-6 h-6 text-[#2E7D5B]" data-id="element-2693" />
              </div>
              <h3 className="font-bold text-[#1a2b4a] mb-2 text-lg" data-id="element-2694">
                Stap voor stap in het tempo van jouw hond
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2695">
                Opgebouwd zodat je hond rustig kan leren, zonder overprikkeling.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start" data-id="element-2696">
              <div className="bg-blue-50 p-3 rounded-full mb-4" data-id="element-2697">
                <GraduationCap className="w-6 h-6 text-blue-600" data-id="element-2698" />
              </div>
              <h3 className="font-bold text-[#1a2b4a] mb-2 text-lg" data-id="element-2699">
                Gebaseerd op de bewezen communicatie-methode
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2700">
                Dezelfde aanpak uit het webinar, specifiek toegepast op de
                wandeling.
              </p>
            </div>
          </div>

          <ul className="space-y-4 md:max-w-2xl md:mx-auto" data-id="element-2701">
            <li className="flex items-start" data-id="element-2702">
              <CheckCircle2 className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-2703" />
              <span className="text-gray-700 font-medium leading-relaxed" data-id="element-2704">
                Levenslang toegang waardoor je vandaag kan starten, maar zelf
                het tempo bepaald
              </span>
            </li>
            <li className="flex items-start" data-id="element-2705">
              <CheckCircle2 className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-2706" />
              <span className="text-gray-700 font-medium leading-relaxed" data-id="element-2707">
                14 dagen niet-goed, geld terug garantie
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* 6. CURRICULUM / WAT ZIT ER IN DE CURSUS? */}
      <section className="py-12 md:py-20 px-4 bg-[#1a2b4a] text-white" data-id="element-2708">
        <div className="max-w-4xl mx-auto" data-id="element-2709">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12 text-center" data-id="element-2710">
            Wat zit er in de cursus?
          </h2>

          <ul className="space-y-4 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-4 md:space-y-0 mb-10" data-id="element-2711">
            <li className="flex items-start" data-id="element-2712">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2713" />
              <div className="flex-1" data-id="element-2714">
                <span className="font-medium" data-id="element-2715">
                  Module 1: Hoe verkeerd riem/lijn/halsband/harnas problemen
                  vergroot
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2716">- €27</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2717">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2718" />
              <div className="flex-1" data-id="element-2719">
                <span className="font-medium" data-id="element-2720">
                  Module 2: Hoe een hond het wandelen ziet
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2721">- €27</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2722">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2723" />
              <div className="flex-1" data-id="element-2724">
                <span className="font-medium" data-id="element-2725">
                  Module 3: Het stresslevel van je hond herkennen (en verlagen)
                  voor ontspannen start
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2726">- €37</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2727">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2728" />
              <div className="flex-1" data-id="element-2729">
                <span className="font-medium" data-id="element-2730">
                  Module 4: Hoe gedragsexperts omgaan met de lijn
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2731">- €47</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2732">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2733" />
              <div className="flex-1" data-id="element-2734">
                <span className="font-medium" data-id="element-2735">
                  Module 5: Zorgen voor maximaal ontspannen (en rustige) hond
                  tijdens wandelen
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2736">- €47</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2737">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2738" />
              <div className="flex-1" data-id="element-2739">
                <span className="font-medium" data-id="element-2740">
                  Module 6: Omgaan met probleemsituaties (technieken van
                  professionals)
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2741">- €47</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2742">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2743" />
              <div className="flex-1" data-id="element-2744">
                <span className="font-medium" data-id="element-2745">
                  Module 7: Trekken aan de lijn aanpakken in drie stappen
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2746">- €37</span>
              </div>
            </li>

            <li className="flex items-start pt-4" data-id="element-2747">
              <span className="text-xl mr-3 flex-shrink-0 mt-0.5" data-id="element-2748">🎁</span>
              <div className="flex-1" data-id="element-2749">
                <span className="font-medium text-blue-200" data-id="element-2750">
                  BONUS 1: "Succesvol Wandelen Start Binnen" Online Checklist
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2751">- €19</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2752">
              <span className="text-xl mr-3 flex-shrink-0 mt-0.5" data-id="element-2753">🎁</span>
              <div className="flex-1" data-id="element-2754">
                <span className="font-medium text-blue-200" data-id="element-2755">
                  BONUS 2: Het Online Stappenplan "In de auto is leuk"
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2756">- €15</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-2757">
              <span className="text-xl mr-3 flex-shrink-0 mt-0.5" data-id="element-2758">🎁</span>
              <div className="flex-1" data-id="element-2759">
                <span className="font-medium text-blue-200" data-id="element-2760">
                  BONUS 3: De Online "5 Kalmerende Activiteiten Voor Tijdens Het
                  Wandelen"
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-2761">- €27</span>
              </div>
            </li>
          </ul>

          {/* COURSE PHOTO CATALOG */}
          <div className="mb-10 md:max-w-2xl md:mx-auto" data-id="element-2762">
            <h3 className="text-xl md:text-2xl font-bold text-center text-white mb-2" data-id="element-2763">
              Kijk alvast in de cursus
            </h3>
            <p className="text-center text-gray-300 text-sm md:text-base mb-6" data-id="element-2764">
              Een blik op de lessen en wat je leert tijdens het wandelen.
            </p>
            <div className="overflow-hidden rounded-2xl bg-black/20 ring-1 ring-white/10" data-id="element-2765">
              <img src={courseImages[selectedImage].src} alt={courseImages[selectedImage].alt} className="w-full aspect-video object-cover" data-id="element-2766" />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 md:gap-3" data-id="element-2767">
              {courseImages.map((image, index) => <button key={image.src} type="button" onClick={() => setSelectedImage(index)} aria-label={`Bekijk foto ${index + 1}`} aria-pressed={selectedImage === index} className={`overflow-hidden rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#e85d75] ${selectedImage === index ? 'ring-2 ring-[#e85d75]' : 'ring-1 ring-white/10 opacity-70 hover:opacity-100'}`} data-id="element-2768">
                  <img src={image.src} alt={image.alt} className="w-full aspect-video object-cover" data-id="element-2769" />
                </button>)}
            </div>
          </div>

          <div className="bg-white/10 rounded-2xl p-6 md:p-8 text-center mb-8 md:max-w-lg md:mx-auto" data-id="element-2770">
            <p className="text-lg text-gray-300 mb-2" data-id="element-2771">Totale waarde: €330</p>
            <p className="text-xl text-gray-400 line-through mb-2" data-id="element-2772">
              Normale prijs: €87
            </p>
            <p className="text-4xl md:text-5xl font-extrabold text-[#e85d75]" data-id="element-2773">
              Vandaag: €43,50
            </p>
          </div>

          <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto md:max-w-md md:mx-auto bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-2774">
            Ja, ik wil de cursus met 50% korting →
          </a>
        </div>
      </section>

      {/* 7. HOE HET WERKT */}
      <section className="py-12 md:py-20 px-4 bg-white" data-id="element-2775">
        <div className="max-w-5xl mx-auto" data-id="element-2776">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1a2b4a] mb-10 md:mb-16" data-id="element-2777">
            Hoe het werkt
          </h2>

          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-3 md:gap-10 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px before:h-full before:w-0.5 before:bg-gray-200 md:before:hidden" data-id="element-2778">
            <div className="relative flex items-start md:flex-col md:items-center md:text-center" data-id="element-2779">
              <div className="w-12 h-12 bg-[#e85d75] text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0 z-10 shadow-md md:mb-5" data-id="element-2780">
                1
              </div>
              <div className="ml-6 pt-2 md:ml-0 md:pt-0" data-id="element-2781">
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-2782">
                  Klik op een rode knop en reken af in onze veilige online
                  winkel
                </p>
              </div>
            </div>

            <div className="relative flex items-start md:flex-col md:items-center md:text-center" data-id="element-2783">
              <div className="w-12 h-12 bg-[#e85d75] text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0 z-10 shadow-md md:mb-5" data-id="element-2784">
                2
              </div>
              <div className="ml-6 pt-2 md:ml-0 md:pt-0" data-id="element-2785">
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-2786">
                  Via de mail ontvang je in enkele minuten inlog tot uitleg over
                  de methode.
                </p>
              </div>
            </div>

            <div className="relative flex items-start md:flex-col md:items-center md:text-center" data-id="element-2787">
              <div className="w-12 h-12 bg-[#e85d75] text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0 z-10 shadow-md md:mb-5" data-id="element-2788">
                3
              </div>
              <div className="ml-6 pt-2 md:ml-0 md:pt-0" data-id="element-2789">
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-2790">
                  Pas de methodes toe en zie hoe jullie wandelingen veranderen
                  naar het leukste moment van de dag!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. PRICING CTA SECTION */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-2791">
        <div className="max-w-lg mx-auto bg-white rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 text-center" data-id="element-2792">
          <div className="inline-block bg-green-100 text-green-800 text-sm font-bold px-4 py-1.5 rounded-full mb-6" data-id="element-2793">
            Je bespaart €43,50
          </div>

          <div className="flex items-center justify-center gap-3 mb-2" data-id="element-2794">
            <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-2795">
              €87
            </span>
            <span className="text-5xl font-extrabold text-[#1a2b4a]" data-id="element-2796">
              €43,50
            </span>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm font-bold text-[#e85d75] mb-8 bg-red-50 py-2 px-4 rounded-lg mt-6" data-id="element-2797">
            <Clock className="w-4 h-4" data-id="element-2798" />
            Actie eindigt over: {formatTime(days)}:{formatTime(hours)}:
            {formatTime(minutes)}:{formatTime(seconds)}
          </div>

          <a href={CHECKOUT_URL} className="flex items-center justify-center w-full bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl mb-6" data-id="element-2799">
            Ja, ik wil de cursus met 50% korting →
          </a>

          <div className="flex flex-col items-center gap-3 text-sm text-gray-500 font-medium" data-id="element-2800">
            <div className="flex items-center gap-4" data-id="element-2801">
              <span className="flex items-center" data-id="element-2802">
                <ShieldCheck className="w-4 h-4 mr-1 text-green-600" data-id="element-2803" /> Veilige
                betaling
              </span>
              <span className="flex items-center" data-id="element-2804">
                <Lock className="w-4 h-4 mr-1 text-gray-400" data-id="element-2805" /> Direct toegang
              </span>
            </div>
            <span className="flex items-center mt-2" data-id="element-2806">
              14 dagen geld-terug garantie
            </span>
          </div>
        </div>
      </section>

      {/* 8b. SOCIAL PROOF - TRUSTPILOT */}
      <section className="py-12 md:py-20 px-4 bg-white" data-id="element-2807">
        <div className="max-w-4xl mx-auto" data-id="element-2808">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1a2b4a] mb-8 md:mb-12" data-id="element-2809">
            Wat andere baasjes zeggen
          </h2>
          <div ref={trustpilotGridRef} className="trustpilot-widget" data-locale="nl-NL" data-template-id="539ad60defb9600b94d7df2c" data-businessunit-id="65017f0060003ffc450ac45e" data-style-height="500px" data-style-width="100%" data-theme="light" data-stars="4,5" data-review-languages="nl" data-id="element-2810">
            <a href="https://nl.trustpilot.com/review/stresslessdogs.nl" target="_blank" rel="noopener noreferrer" data-id="element-2811">
              Trustpilot
            </a>
          </div>
        </div>
      </section>

      {/* 9. FAQ SECTION */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-2812">
        <div className="max-w-3xl mx-auto" data-id="element-2813">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1a2b4a] mb-8 md:mb-12" data-id="element-2814">
            Veelgestelde vragen
          </h2>
          <div className="space-y-3" data-id="element-2815">
            {faqs.map((faq, i) => <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-[#FAFAF7]" data-id="element-2816">
                <button onClick={() => toggleFaq(i)} className="w-full flex justify-between items-center p-5 text-left hover:bg-gray-50 transition-colors" data-id="element-2817">
                  <span className="font-bold text-[#1a2b4a] pr-4" data-id="element-2818">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-2819" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-2820" />}
                </button>
                {openFaq === i && <div className="p-5 pt-0 text-gray-600 bg-[#FAFAF7] leading-relaxed" data-id="element-2821">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA SECTION */}
      <section className="py-16 md:py-24 px-4 bg-[#1a2b4a] text-white text-center" data-id="element-2822">
        <div className="max-w-2xl mx-auto" data-id="element-2823">
          <p className="text-lg md:text-xl text-blue-200 mb-6 font-medium" data-id="element-2824">
            Na zondag 28 juni 23:59 is de prijs weer €87
          </p>

          <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto md:inline-flex md:mx-auto bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl mb-10" data-id="element-2825">
            Ja, ik wil de cursus met 50% korting →
          </a>

          <div className="flex flex-col items-center" data-id="element-2826">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20 mb-4" data-id="element-2827">
              <img src="https://cdn.magicpatterns.com/uploads/m4muL5gCGsQb9Rc66zAJPA/Amee_wandelen_foto2.png" alt="Amee de Bruijn" className="w-full h-full object-cover" data-id="element-2828" />
            </div>
            <p className="font-serif text-2xl text-blue-100" data-id="element-2829">Liefs, Amee</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-10 px-4 border-t border-gray-200" data-id="element-2830">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:justify-between items-center gap-6" data-id="element-2831">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6YKzftRpGkz4jKTtn8FQxB/Logo_Stressless_Dogs_%288%29_%284%29.png" alt="Stressless Dogs" className="h-8 object-contain" data-id="element-2832" />
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500" data-id="element-2833">
            <a href="#" className="hover:text-gray-900" data-id="element-2834">
              Privacybeleid
            </a>
            <a href="#" className="hover:text-gray-900" data-id="element-2835">
              Algemene Voorwaarden
            </a>
            <a href="mailto:amee@stresslessdogs.nl" className="hover:text-gray-900" data-id="element-2836">
              Contact
            </a>
          </div>
          <p className="text-sm text-gray-400" data-id="element-2837">
            © {new Date().getFullYear()} Stressless Dogs
          </p>
        </div>
      </footer>
    </div>;
}